<template>
  <v-container class="d-flex justify-center align-center pt-10">
    <attachment-dialog
    v-if="attachmentDialog.model"
    v-model="attachmentDialog.model"
    :attachments.sync="attachmentDialog.attachments"
    :preview="isReadOnly"/>

    <v-card
    v-if="isDataReady"
    width="500"
    min-height="780"
    class="rounded-0 primary-background-color px-8 pb-10"
    elevation="5">
      <v-form
      v-model="isFormValid">
        <v-row
        dense
        no-gutters>
          <v-col class="pt-8">
            <div
            v-if="!isReadOnly"
            class="new-user-title-box d-flex justify-center align-center">
              <v-icon dark class="mr-2">
                mdi-lead-pencil
              </v-icon>
              <span>{{$t('pages.remoteSupport.cardTitle')}}</span>
            </div>
            <waiting-approval-label
            v-else
            :status="previewData.initialApproval"
            box/>
          </v-col>
        </v-row>
        <v-row
        v-if="isRole(['SERVICE']) && !isReadOnly"
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <async-text-field-autocomplete-clients
            v-model="client"
            label="textFields.clients"
            item-text="name"
            required
            :service-identification="getIdentification"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <p class="process-repair-tab-section-subtitle">
              {{$t('textFields.specialSupport')}}
            </p>
            <text-field-select
            v-model="newData.specialSupport"
            :items="specialSupportOptions"
            required
            :readonly="isReadOnly"
            :label="$t('textFields.selectAnAnswer')"
            item-text="text"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-field-value
            v-model="newData.title"
            required
            :readonly="isReadOnly"
            :label="$t('textFields.title')"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-area
            v-model="newData.description"
            no-resize
            required
            :readonly="isReadOnly"
            :label="$t('textFields.description')"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-field-date
            v-model="newData.preferredDate"
            :readonly="isReadOnly"
            required
            :label="$t('textFields.preferredDate', {'0': getTimeZoneInNumber(new Date())})"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-field-time
            v-model="newData.preferredTime"
            :readonly="isReadOnly"
            required
            :label="$t('textFields.preferredTime', {'0': getTimeZoneInNumber(new Date())})"/>
          </v-col>
        </v-row>
        <v-row
        v-if="!isRequestBeforePreferredContactMethodsField"
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <p class="process-repair-tab-section-subtitle">
              {{$t('textFields.preferredContactMethod')}}
            </p>
            <preferred-contact-methods
            ref="preferredContactMethodsRef"
            v-model="newData.preferredContactMethods"
            :readonly="isReadOnly"
            required
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <p class="process-repair-tab-section-subtitle">
              {{$t('textFields.attachments')}}
            </p>
            <div class="d-flex justify-center">
              <attachments-dialog-trigger
              @click="openAttachmentsDialog"
              :attachments="newData.attachmentsByClient"
              :disable-on-empty="isReadOnly"
              :editable="!isReadOnly"/>
            </div>
          </v-col>
        </v-row>

        <p class="process-repair-tab-section-title" style="font-size: 20px; position: relative; top: 20px;">{{$t('pages.repair.contactDetails')}}</p>

        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-field-first-name
            v-model="newData.contact.firstName"
            :readonly="isReadOnly"
            required
            :label="$t('textFields.firstName')"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-field-last-name
            v-model="newData.contact.lastName"
            :readonly="isReadOnly"
            required
            :label="$t('textFields.lastName')"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-field-phone
            v-model="newData.contact.phone"
            :readonly="isReadOnly"
            required
            :label="$t('textFields.phone')"/>
          </v-col>
        </v-row>
        <v-row
        class="mt-8"
        dense
        no-gutters>
          <v-col>
            <text-field-email
            v-model="newData.contact.email"
            :readonly="isReadOnly"
            required
            :label="$t('textFields.email')"/>
          </v-col>
        </v-row>

        <v-row
        :class="`mt-8`"
        dense
        no-gutters
        v-for="(e, i) in newData.contact.backupEmails" 
        :key="i">
          <v-col>
            <text-field-email
            v-model="e.value"
            :readonly="isReadOnly"
            required
            :append-icon="isReadOnly ? '' : 'mdi-minus'"
            @click:append="removeBackupEmail(i)"
            :label="$t('textFields.email')"/>
          </v-col>
        </v-row>
        <v-row
        v-if="!isReadOnly"
        class="mt-8"
        dense
        no-gutters>
          <v-col class="d-flex justify-center align-center">
            <v-btn
            @click="addBackupEmail"
            icon>
              <v-icon color="primary">
                mdi-plus
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-row
        v-if="!isReadOnly"
        class="mt-8"
        dense
        no-gutters>
          <v-col class="d-flex justify-center align-center">
            <primary-btn
            @click="openConfirmDialog"
            :disabled="!isFormValid"
            :label="$t('btns.create')"
            :loading="isLoading"
            color='#0277bd'
            :width="150"
            :height="50"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </v-container>
</template>
<script>
import WaitingApprovalLabel from '@/components/common/process-tabs/WaitingApprovalLabel'
import AsyncTextFieldAutocompleteClients from '@/components/text-fields/AsyncTextFieldAutocompleteClients'
import TextFieldValue from '@/components/text-fields/TextFieldValue'
import TextArea from '@/components/text-fields/TextArea'
import TextFieldFirstName from '@/components/text-fields/TextFieldFirstName'
import TextFieldLastName from '@/components/text-fields/TextFieldLastName'
import TextFieldPhone from '@/components/text-fields/TextFieldPhone'
import TextFieldEmail from '@/components/text-fields/TextFieldEmail'
import TextFieldDate from '@/components/text-fields/TextFieldDate'
import TextFieldTime from '@/components/text-fields/TextFieldTime'
import PrimaryBtn from '@/components/btns/PrimaryBtn'
import TextFieldSelect from '@/components/text-fields/TextFieldSelect'
import AttachmentsDialogTrigger from '@/components/common/triggers/AttachmentsDialogTrigger'
import AttachmentDialog from '@/components/dialogs/AttachmentDialog'
import PreferredContactMethods from '@/components/remote-support/PreferredContactMethods'
import { createRemoteSupport } from '@/repositories/supportRepo'
import { execHttpRequestThrowable } from '@/services/http'
import { routeFromRoot } from '@/services/routeService'
import DateUtil from '@/utils/DateUtil'
import { copyObj } from '@/utils/StringUtil'
import { getUserIdentification, getUserRole } from '@/services/authService'
export default {
  components: {
    WaitingApprovalLabel,
    AsyncTextFieldAutocompleteClients,
    TextFieldValue,
    TextArea,
    TextFieldFirstName,
    TextFieldLastName,
    TextFieldPhone,
    TextFieldEmail,
    TextFieldDate,
    TextFieldTime,
    TextFieldSelect,
    PrimaryBtn,
    PreferredContactMethods,
    AttachmentsDialogTrigger,
    AttachmentDialog
  },
  props: {
    previewData: {
      type: Object,
      default: () => {
        return null
      }
    }
  },
  data() {
    return {
      newData: {
        title: '',
        description: '',
        specialSupport: null,
        preferredDate: null,
        preferredTime: null,
        preferredContactMethods: [],
        attachmentsByClient: [],
        
        contact: {
          id: null,
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          backupEmails: []
        },
      },
      attachmentDialog: {
        model: false
      },
      isDataReady: false,
      client: null,
      isFormValid: false,
      isLoading: false,
      specialSupportOptions: [
        {
          text: this.$t("textFields.simpleYes"),
          value: true
        }, {
          text: this.$t("textFields.simpleNo"),
          value: false
        }
      ]
    }
  },
  computed: {
    isReadOnly() {
      return !!this.previewData
    },
    role() {
      return getUserRole()
    },
    getIdentification() {
      return getUserIdentification()
    },
    isRequestBeforePreferredContactMethodsField() {
      return !!this.previewData && this.previewData.preferredContactMethods.length === 0
    }
  },
  methods: {
    addBackupEmail() {
      this.newData.contact.backupEmails.push({
        id: null,
        value: ''
      })
    },
    removeBackupEmail(i) {
      this.newData.contact.backupEmails.splice(i, 1)
    },
    isRole(roles) {
      return roles.includes(this.role)
    },
    openConfirmDialog() {
      if (!this.$refs.preferredContactMethodsRef.validate()) return

      this.$confirmActionDialog.open(
      'dialogs.titles.createRemoteSupportRequest', 
      null, 
      'btns.create',
      null,
      this.tryToCreateNewRemoteSupport)
    },
    async tryToCreateNewRemoteSupport() {
      const data = {
        title: this.newData.title,
        description: this.newData.description,
        specialSupport: this.newData.specialSupport.value,
        preferredDate: new Date(`${this.newData.preferredDate}T${this.newData.preferredTime}:00`).toUTCString(),
        preferredContactMethods: this.newData.preferredContactMethods,
        contact: this.newData.contact,
        client: this.client,
        attachments: this.newData.attachmentsByClient
      }
      // console.log(data)
      await execHttpRequestThrowable(async () => await createRemoteSupport(data), {}, v => this.isLoading = v)
      routeFromRoot('remote-support/list')
    },
    putContactData(data) {
      let cc
      if (data) {
        cc = data
      }
      else cc = this.$contactHolder.get()
      this.newData.contact = {
        firstName: String(cc.firstName),
        lastName: String(cc.lastName),
        phone: String(cc.phone),
        email: String(cc.email),
        backupEmails: [...cc.backupEmails]
      }
    },
    getTimeZoneInNumber(d) {
      return DateUtil.getTimeZoneInNumber(d)
    },
    openAttachmentsDialog() {
      this.attachmentDialog = {
        model: true,
        attachments: this.newData.attachmentsByClient
      }
    }
  },
  mounted() {
    this.isDataReady = false
    if (this.previewData) {
      this.newData = copyObj(this.previewData)
      this.newData.specialSupport = this.specialSupportOptions.filter(o => o.value === this.previewData.specialSupport)[0]
    }
    this.$feedback.fill("Create Remote Support Page", this.$route.path)
    this.isDataReady = true
  },
  created() {
    if (this.isRole(["SUPERVISOR", "SERVICE"]) && !this.isReadOnly) {
      routeFromRoot("action-required")
      return
    }

    if (this.isRole(['CLIENT'])) {
      this.$watch('$contactHolder.contact', () => {
        this.putContactData()
      }, {
        immediate: true,
        deep: true
      })
    }
    //NOTE: this is not in use, because we disabled the SERVICE role user interaction with this functionallity.
    else {
      this.$watch('client', (v) => {
        if (v) this.putContactData(v.contact)
      }, {
        deep: true
      })
    }
  }
}
</script>
<style>

</style>